/**
 * RAVEN - Auction Tool: EditPackage.jsx
 * [Main Edit/View component for an Auction Package.]
 * - Handles editing of package details, including package items and bidders.
 * - Supports both "edit" and "liveAuction" modes.
 * - Interacts with Redux and custom hooks for all actions.
 */

import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import validator from "validator";

// Custom hooks
import { useAuctionsData } from "src/hooks/useAuctionsData";
import { useLookupsData } from "src/context/LookupsContext";
import { useDialogBox } from "src/hooks/useDialogBox";
import { useAuctionActions } from "src/hooks/useAuctionActions";
import { useBaselinesData } from "src/hooks/useBaselinesData";
import { useBaselineItemsData } from "src/hooks/useBaselineItemsData";
import { useDirtyFormGuard } from "src/hooks/useDirtyFormGuard";

// Layout/UI
import { NumericFormatAdapter } from "../UI/common/NumericFormatAdapter";
import { AppModal } from "src/components/UI/common/AppModal";
import {
	Box,
	createListCollection,
	Field,
	Input,
	MetreaSelectContent as SelectContent,
	MetreaSelectItem as SelectItem,
	MetreaSelectLabel as SelectLabel,
	MetreaSelectRoot as SelectRoot,
	MetreaSelectTrigger as SelectTrigger,
	MetreaSelectValueText as SelectValueText,
	Portal,
	Stack,
} from "@metrea-ui/v2";
import { ModalShell } from "src/components/UI/common/ModalShell";
import FormTextField from "src/components/UI/common/FormTextField";
import RecordNav from "../UI/RecordNav/RecordNav";
import PackageItemList from "./PackageItemList";
import BaselineItemConfigs from "../Baseline/BaselineItemConfigs";
import PackageItemDetails from "./EditPackageItem";
import PackageBidderDetails from "../User/EditUser";
import PackageBidderList from "./PackageBidderList";

// Utilities
import { Session, isAdmin, isDevTeam } from "src/utils/sessionUtils";
import { debugLog } from "src/utils/debugUtils";
import { findObjectByProperty, getValuesByPropertyPath, deepClone } from "src/utils/dataUtils";
import { handleDecimalsOnValue } from "src/utils/formatUtils";
import { exportSpreadsheet } from "src/utils/fileUtils";
import { baselineAccess, extendBaseline } from "src/utils/baselineUtils";

// Constants
import { initialAuctionPackageState } from "src/constants/initialStates";

const getSelectLabel = (item) =>
	item?.label ??
	item?.extended_name ??
	item?.display_name ??
	item?.name ??
	item?.comp_value ??
	(item?.code && item?.symbol ? `${item.code} - ${item.symbol}` : undefined) ??
	item?.code ??
	item?.title ??
	item?.text ??
	String(item?.id ?? "");

const sizeToSelectSize = (size) => {
	if (size === "large") return "md";
	if (size === "small") return "sm";
	return size || "sm";
};

const getSelectValue = (item) => String(item?.id ?? item?.value ?? "");

const buildSelectCollectionItems = ({ items, includeEmpty = false, placeholder = "" }) => [
	...(includeEmpty ? [{ label: placeholder, value: "" }] : []),
	...items.map((item) => ({
		label: getSelectLabel(item),
		value: getSelectValue(item),
	})),
];

const sizeToFieldControl = (size) => {
	if (size === "large") return "md";
	return "sm";
};

const renderSelectValueLabel = (selectedItems) => getSelectLabel(selectedItems?.[0]);

const NAME_REQUIRED_ERROR = "Name is required!";
const BASELINE_REQUIRED_ERROR = "Please choose a baseline to continue";

// Selection order is a table/UI concern; compare a stable ID set for dirty-state checks.
const normalizeSelectedIds = (values) =>
	(Array.isArray(values) ? values : []).map((value) => String(value)).sort();

const validatePackageMetadata = (auctionPackage) => ({
	nameError:
		typeof auctionPackage?.name === "string" && auctionPackage.name.trim() !== ""
			? ""
			: NAME_REQUIRED_ERROR,
	baselineError:
		auctionPackage?.baseline_id !== null &&
		auctionPackage?.baseline_id !== undefined &&
		auctionPackage?.baseline_id !== ""
			? ""
			: BASELINE_REQUIRED_ERROR,
});

const EditPackage = (props) => {
	const renderCount = useRef(0);
	renderCount.current++;

	const pageWidth = "95%";
	const pageMode = props.mode;
	const uiSize = props.size;

	// --- Redux Selectors ---
	// - selectedPkg is the current package (or null if on the list page)
	const selectedPkg = useSelector((state) => state.auctions.selectedPkg);
	const baselines = useSelector((state) => state.baselines?.list ?? []);
	const selectedPackageItem = useSelector((state) => state.auctions.selectedPkgItem);

	// const [localPackage, setLocalPackage] = useState(deepClone(props.item));
	// const [currBaseline, setCurrBaseline] = useState(deepClone(props.item.baseline));
	const [localPackage, setLocalPackage] = useState(deepClone(selectedPkg) || initialAuctionPackageState);
	const [currBaseline, setCurrBaseline] = useState(deepClone(selectedPkg?.baseline || {}));
	const [currBidder, setCurrBidder] = useState();
	const [originalBidder, setOriginalBidder] = useState(null);
	const [currFund, setCurrFund] = useState(0);
	const [modalOpen, setModalOpen] = useState(false);
	const [baselinesFiltered, setBaselinesFiltered] = useState([]);
	const [nameError, setNameError] = useState(
		() => validatePackageMetadata(localPackage).nameError
	);
	const [fundError, setFundError] = useState("");
	const [baselineError, setBaselineError] = useState(
		() => validatePackageMetadata(localPackage).baselineError
	);

	const syncPackageMetadataValidation = (auctionPackage) => {
		const validation = validatePackageMetadata(auctionPackage);
		setNameError(validation.nameError);
		setBaselineError(validation.baselineError);
	};

	// Custom hook abstracts Redux logic for user retrieval
	const { lookups, allUsers, allUserGroups } = useLookupsData();
	const { withConfirmation } = useDialogBox();
	const {
		fetchBaselines,
	} = useBaselinesData();

	const {
		fetchAuctionPackages,
		fetchAuctionPackageByIdOrUuid,
		fetchAuctionPackageItemById,
		fetchTopAutoBids,
		clearSelectedPackage,
	} = useAuctionsData();

	const {
		updateAuctionPackageAction,
		createAuctionPackageAction,
		updateAuctionPackageStatusAction,
		updatePackageBidderAction,
	} = useAuctionActions({
		fetchAuctionPackageByIdOrUuid,
		fetchAuctionPackages,
		allUsers,
		clearDataError: null,
		handleBackToList,
		localAuctionPackage: localPackage,
		selectedPkg: null,
		setLocalAuctionPackage: setLocalPackage,
	});

	const {
		// itemList,
		currentItem,
		setCurrentItem,
		// handleSelectBaselineItem,
		// clearCurrentItem,
		fetchAndPrepareItem,
	} = useBaselineItemsData(localPackage?.baseline, lookups);

	useEffect(() => {
		debugLog(2, "[EditPackage][FLOW] mounted", {});
		if (!localPackage?.id) fetchBaselines();
		if (!localPackage?.id) {
			debugLog(2, "[EditPackage][FLOW] fetchBaselines started", {});
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		// setLocalPackage({ ...props.item });
		// setLocalPackage(deepClone(props.item));  // slow?
		if (selectedPkg) {
			const replacementPackage = deepClone(selectedPkg);
			setLocalPackage(replacementPackage);
			setCurrBaseline(deepClone(replacementPackage?.baseline || {}));
			syncPackageMetadataValidation(replacementPackage);
		}
		debugLog(3, "[EditPackage][STATE] useEffect selectedPkg", {
			selectedPkgId: selectedPkg?.id,
		});

		// }, [props.item]);
	}, [selectedPkg]);

	// --- Effect: Filter baselines based on access, to prepare for baseline dropdown
	useEffect(() => {
		if (baselines && baselines.length > 0) {
			const filtered = baselines.filter(
				(baseline) => baselineAccess(baseline, allUserGroups, Session().currUser) >= 1
			);

			// add additional baseline info to the name
			const filteredClone = filtered.map(extendBaseline);
			debugLog(4, "[EditPackage][PROCESS] baselinesFiltered recalculated", {
				filteredClone: filteredClone,
			});

			setBaselinesFiltered(filteredClone);
		}
	}, [baselines, allUserGroups]);

	const originalPackageSnapshot = useMemo(
		() => ({
			name: selectedPkg?.name ?? "",
			description: selectedPkg?.description ?? "",
			comments: selectedPkg?.comments ?? "",
			baseline_id: selectedPkg?.baseline_id ?? null,
			aucItemSelected: normalizeSelectedIds(selectedPkg?.aucItemSelected),
			aucBidderSelected: normalizeSelectedIds(selectedPkg?.aucBidderSelected),
		}),
		[selectedPkg]
	);

	const localPackageSnapshot = useMemo(
		() => ({
			name: localPackage?.name ?? "",
			description: localPackage?.description ?? "",
			comments: localPackage?.comments ?? "",
			baseline_id: localPackage?.baseline_id ?? null,
			aucItemSelected: normalizeSelectedIds(localPackage?.aucItemSelected),
			aucBidderSelected: normalizeSelectedIds(localPackage?.aucBidderSelected),
		}),
		[localPackage]
	);

	const hasValidationError = nameError !== "" || baselineError !== "";
	const { canSave, canRevert } = useDirtyFormGuard({
		originalValue: originalPackageSnapshot,
		currentValue: localPackageSnapshot,
		hasError: hasValidationError,
	});

	const originalBidderSnapshot = useMemo(
		() => ({
			// The bidder modal only edits fund in this workflow, so keep the dirty comparison
			// scoped to the value that can actually change before save.
			fund: originalBidder?.fund == null ? "" : String(originalBidder.fund),
		}),
		[originalBidder]
	);

	const currentBidderSnapshot = useMemo(
		() => ({
			fund: currBidder?.fund == null ? "" : String(currBidder.fund),
		}),
		[currBidder]
	);

	const {
		canSave: canSaveBidder,
		canRevert: canRevertBidder,
	} = useDirtyFormGuard({
		originalValue: originalBidderSnapshot,
		currentValue: currentBidderSnapshot,
		hasError: fundError !== "",
	});

	// Auction plain-text fields now share the stable direct-v2 helper, while the
	// numeric helper stays local for package-specific validation and formatting.
	const renderTextField = (props) => (
		<FormTextField {...props} size={sizeToFieldControl(uiSize)} helperColor="#F04438" />
	);

	const renderNumericField = ({
		id,
		name,
		label,
		value,
		onChange,
		disabled = false,
		required = false,
		errorText = "",
		placeholder,
		width = "100%",
		formatSpec,
	}) => {
		const invalid = Boolean(errorText);
		const helpId = `${id}-helptext`;
		const controlSize = sizeToFieldControl(uiSize);
		const helperVisible = Boolean(errorText);
		const controlStyle = {
			width: "100%",
			fontSize: controlSize === "sm" ? "0.8rem" : "0.875rem",
		};

		return (
			<Box width={width} mt="1rem" mb="0.5rem">
				<Field.Root required={required} invalid={invalid} style={{ width: "100%" }}>
					<Field.Label htmlFor={id}>
						{label}
						{required ? <Field.RequiredIndicator /> : null}
					</Field.Label>
					<NumericFormatAdapter
						customInput={Input}
						id={id}
						name={name}
						value={value ?? ""}
						disabled={disabled}
						placeholder={placeholder}
						size={controlSize}
						variant="subtle"
						aria-invalid={invalid ? "true" : undefined}
						aria-describedby={helpId}
						onChange={onChange}
						formatSpec={formatSpec}
						style={controlStyle}
					/>
					<Field.HelperText id={helpId}>
						<span
							style={{
								display: "block",
								minHeight: "1.5em",
								fontSize: "0.75rem",
								color: invalid ? "#F04438" : "inherit",
								visibility: helperVisible ? "visible" : "hidden",
							}}
						>
							{helperVisible ? errorText : "\u00A0"}
						</span>
					</Field.HelperText>
				</Field.Root>
			</Box>
		);
	};

	// --- Utility Functions ---
	const isNew = () => !localPackage?.id;
	const clearCurrItem = () => {
		setCurrentItem(null);
	};

	// --- Handlers ---
	const handleModalClose = () => {
		debugLog(3, "[EditPackage][ACTION] handleModalClose", {});

		setCurrentItem(null);
		setCurrBidder(null);
		setOriginalBidder(null);
		setFundError("");
		setModalOpen(false);
	};

	function handleBackToList() {
		debugLog(3, "[EditPackage][ACTION] handleBackToList", {});

		clearSelectedPackage();
		setLocalPackage(null);
		debugLog(2, "[EditPackage][FLOW] fetchAuctionPackages started", {});
		fetchAuctionPackages();
	}

	const handleReloadList = () => {
		// reload auction package to get the updated lists of items and bidders
		debugLog(3, "[EditPackage][ACTION] handleReloadList", { packageId: localPackage?.id });
		if (!localPackage?.id) return;

		debugLog(2, "[EditPackage][FLOW] fetchAuctionPackageByIdOrUuid started", {
			idOrUuid: localPackage?.id,
		});
		fetchAuctionPackageByIdOrUuid(localPackage?.id);
		setCurrBidder(null);
	};

	const handleInputChange = (event) => {
		const { name, value } = event.target;
		debugLog(7, "[EditPackage][ACTION] handleInputChange", { name: name, value: value });

		const updatedPackage = {
			...localPackage,
			[name]: value,
			updatedAt: new Date(),
			updatedBy: Session().currUser.id,
		};
		setLocalPackage(updatedPackage);
		syncPackageMetadataValidation(updatedPackage);
	};

	const handleBidderInputChange = (event) => {
		const { name, value } = event.target;
		debugLog(7, "[EditPackage][ACTION] handleBidderInputChange", { name: name, value: value });

		setCurrBidder({
			...currBidder,
			[name]: value,
			updatedAt: new Date(),
			updatedBy: Session().currUser.id,
		});

		if (name === "fund") {
			if (
				value &&
				value.trim() &&
				validator.isDecimal(value.trim()) &&
				validator.isFloat(value.trim(), { min: 0 })
			) {
				setFundError("");
				const decimalValue = handleDecimalsOnValue(value);
				setCurrFund(decimalValue);
			} else {
				setFundError("A valid fund amount is required! (i.e. greater or equal to 0)");
			}
		}
	};

	const handleUpdateStatus = (newStatus) => {
		// event.preventDefault();
		if (!localPackage?.id) return;

		debugLog(3, "[EditPackage][ACTION] handleUpdateStatus", {
			newStatus: newStatus,
			packageId: localPackage?.id,
		});

		let arrayMsg = [
			`You are about to archive this package, ${localPackage.name}.`,
			"This package would be removed from for the general users.",
			" ",
			"Do you want to proceed?",
		];

		if (newStatus !== 1) {
			withConfirmation(
				{
					title: "Warning",
					message: arrayMsg,
				},
				() => updateAuctionPackageStatusAction(localPackage?.id, newStatus)
			);
		} else {
			updateAuctionPackageStatusAction(localPackage?.id, newStatus);
		}
	};

	const handleDumpTopAutoBids = (event) => {
		debugLog(3, "[EditPackage][ACTION] handleDumpTopAutoBids", { packageId: localPackage?.id });
		if (!localPackage?.id) return;

		debugLog(2, "[EditPackage][FLOW] fetchTopAutoBids started", { packageId: localPackage?.id });
		fetchTopAutoBids(localPackage?.id).then((data) => {
			debugLog(4, "[EditPackage][PROCESS] fetchTopAutoBids result", {
				packageId: localPackage?.id,
				data: data,
			});
			// Do not create an export when retrieval failed and returned no data.
			if (data == null) return;

			// Output in XLSX format
			const finalO = { "Top Auto Bids": data };
			exportSpreadsheet(finalO, "xlsx", "Top Auto Bids");

			// trackEvent({
			// 	event: `Top Auto Bids data was dumped`,
			// 	props: null,
			// });
		});
	};

	const handleSelectPackageItemForAuction = async (item) => {
		debugLog(3, "[EditPackage][ACTION] handleSelectPackageItemForAuction", { item: item });

		// get selectedPackageItem populated to show Auction Page for the item
		debugLog(2, "[EditPackage][FLOW] fetchAuctionPackageItemById started", {
			id: item?.package_item_id,
		});
		await fetchAuctionPackageItemById(item.package_item_id);
	};

	const handleSelectItem = (selected) => {
		debugLog(3, "[EditPackage][ACTION] handleSelectItem", { selected: selected });
		setLocalPackage((currentPackage) => ({ ...currentPackage, aucItemSelected: selected }));
	};

	const handleShowItem = (item) => {
		debugLog(3, "[EditPackage][ACTION] handleShowItem", { item: item });

		// get currItem populated to show Baseline Item
		debugLog(2, "[EditPackage][FLOW] fetchAndPrepareItem started", { id: item?.id });
		fetchAndPrepareItem(item.id);
		setModalOpen(true);
	};

	const handleSelectBidder = (selected) => {
		debugLog(3, "[EditPackage][ACTION] handleSelectBidder", { selected: selected });
		setLocalPackage((currentPackage) => ({ ...currentPackage, aucBidderSelected: selected }));
	};

	const handleEditBidder = (selected) => {
		debugLog(3, "[EditPackage][ACTION] handleEditBidder", { selected: selected });
		const bidder = findObjectByProperty(localPackage.auc_package_bidders, "user_id", selected.id);

		// get currBidder populated
		const bidderForEdit = { ...bidder, user: selected };
		setOriginalBidder({ ...bidderForEdit });
		setCurrBidder(bidderForEdit);
		setCurrFund(bidder.fund || 0);
		setFundError("");
		setModalOpen(true);
	};

	const handleSaveBidder = () => {
		debugLog(3, "[EditPackage][ACTION] handleSaveBidder", { currBidder: currBidder });

		debugLog(2, "[EditPackage][FLOW] updatePackageBidderAction started", {
			bidderId: currBidder?.id,
		});
		updatePackageBidderAction({
			currBidder,
			setCurrBidder,
		});
	};

	const handleRevertBidder = () => {
		debugLog(3, "[EditPackage][ACTION] handleRevertBidder", {
			bidderId: originalBidder?.id,
		});

		if (!originalBidder) return;

		setCurrBidder({ ...originalBidder });
		setCurrFund(originalBidder.fund || 0);
		setFundError("");
	};

	const handleChooseBaseline = (name, selected) => {
		debugLog(3, "[EditPackage][ACTION] handleChooseBaseline", { name: name, selected: selected });

		const updatedPackage = {
			...localPackage,
			[name]: selected,
			updatedAt: new Date(),
			updatedBy: Session().currUser.id,
		};
		setLocalPackage(updatedPackage);
		syncPackageMetadataValidation(updatedPackage);
	};

	const clickArchiveHandler = (event, newStatus) => {
		debugLog(3, "[EditPackage][ACTION] clickArchiveHandler", { event: event, newStatus: newStatus });

		handleUpdateStatus(newStatus);
	};

	const clickSaveHandler = (event) => {
		event.preventDefault();

		const cPackage = {
			...localPackage,
			updatedAt: new Date(),
			updatedBy: Session().currUser.id,
		};

		debugLog(3, "[EditPackage][ACTION] clickSaveHandler", { cPackage: cPackage, isNew: isNew() });

		if (isNew()) {
			debugLog(2, "[EditPackage][FLOW] createAuctionPackageAction started", {});
			createAuctionPackageAction();
		} else {
			debugLog(2, "[EditPackage][FLOW] updateAuctionPackageAction started", {
				packageId: localPackage?.id,
			});
			updateAuctionPackageAction();
		}
	};

	const clickRevertHandler = (event) => {
		event?.preventDefault?.();
		debugLog(3, "[EditPackage][ACTION] clickRevertHandler", {
			packageId: selectedPkg?.id,
		});

		const revertedPackage = deepClone(selectedPkg) || deepClone(initialAuctionPackageState);
		setLocalPackage(revertedPackage);
		setCurrBaseline(deepClone(revertedPackage?.baseline || {}));
		setCurrBidder(null);
		setModalOpen(false);
		syncPackageMetadataValidation(revertedPackage);
	};

	const clickBackToListHandler = () => {
		debugLog(3, "[EditPackage][ACTION] clickBackToListHandler", {});
		handleBackToList();
	};

	const clickDataDumpHandler = (option) => {
		debugLog(3, "[EditPackage][ACTION] clickDataDumpHandler", {});
		handleDumpTopAutoBids(option, 1);
	};

	// --- Logging: Render and State Snapshots ---
	debugLog(9, "[EditPackage][FLOW] render", {
		renderCount: renderCount.current,
		localPackageId: localPackage?.id,
		currBaselineId: currBaseline?.id,
		currBidderId: currBidder?.id,
		selectedPkgId: selectedPkg?.id,
		selectedPackageItemId: selectedPackageItem?.id,
		baselineCount: baselines?.length ?? 0,
		baselinesFilteredCount: baselinesFiltered?.length ?? 0,
		nameError: nameError,
		fundError: fundError,
		baselineError: baselineError,
		modalOpen: modalOpen,
		pageMode: pageMode,
	});

	return (
		<div>
			{/* display Baseline Item details */}
			{modalOpen && currentItem && (
				<AppModal open={modalOpen} onClose={handleModalClose}>
					<Box>
						<ModalShell>
							<BaselineItemConfigs
								formFieldWidth={"100%"}
								baseline={currBaseline}
								item={currentItem}
								mode="auction"
								size={uiSize || "small"}
								onLocalItemChange={() => void 0}
								onClearCurrentItem={clearCurrItem}
								onSaveDataToDB={() => void 0}
								onClearComplete={() => void 0}
								onReloadCurrentItem={() => void 0}
								onSetDataChanged={() => void 0}
							/>
						</ModalShell>
					</Box>
				</AppModal>
			)}

			{/* display Bidder modal - bidder details, ex. Bidding Fund */}
			{modalOpen && currBidder && (
				<AppModal open={modalOpen} onClose={handleModalClose}>
					<Box>
						<ModalShell inset="20%">
							<Box>
								<Stack>
									<PackageBidderDetails
										item={currBidder.user}
										callFrom="Package Bidder"
										mode="view"
										onReloadList={handleReloadList}
										onClickSave={handleSaveBidder}
										onClickRevert={handleRevertBidder}
										disabledSave={!canSaveBidder}
										disabledRevert={!canRevertBidder}
										hasError={fundError !== ""}
									/>
									{renderNumericField({
										id: "fund",
										name: "fund",
										label: "Bidding Fund",
										required: true,
										placeholder: "Bidding Fund",
										value: currFund,
										onChange: handleBidderInputChange,
										disabled: pageMode !== "edit",
										errorText: fundError,
										formatSpec: {
											kind: "currency",
											symbol: "$",
											unit: "",
											decimals: 2,
											fixed: true,
											allowNegative: false,
										},
									})}
								</Stack>
							</Box>
						</ModalShell>
					</Box>
				</AppModal>
			)}

			{/* display auction package details, including PackageItemList and PackageBidderList */}
			{localPackage && !selectedPackageItem && (
				<>
					<RecordNav
						callFrom="Auction Package"
						showBack
						{...(pageMode === "edit"
							? {
									...(localPackage.status_id !== 1
										? { showRestore: true }
										: { showArchive: true }),
									...(isAdmin() || isDevTeam()
										? {
												showDataDump: true,
												dataDumpToolTip: "Export Top Auto Bids (.xlsx)",
										  }
										: {}),
									showRevert: true,
									showSave: true,
							  }
							: {})}
						onClickBackToList={clickBackToListHandler}
						onClickDataDump={clickDataDumpHandler}
						onClickArchive={clickArchiveHandler}
						onClickRevert={clickRevertHandler}
						onClickSave={clickSaveHandler}
						disabledRevert={!canRevert}
						disabledSave={!canSave}
						hasError={hasValidationError}
					/>
					<hr />
					{/* <Box m={3} /> */}

					<Stack>
						{renderTextField({
							id: "name",
							name: "name",
							label: "Package Name",
							required: true,
							placeholder: "Package Name",
							value: localPackage.name,
							onChange: handleInputChange,
							disabled: pageMode !== "edit",
							errorText: nameError,
						})}
						{renderTextField({
							id: "description",
							name: "description",
							label: "Description",
							value: localPackage?.description ?? "",
							onChange: handleInputChange,
							disabled: pageMode !== "edit",
							multiline: true,
							rows: 6,
						})}

						{renderTextField({
							id: "comments",
							name: "comments",
							label: "Note/Comments",
							placeholder: "Note/Comments",
							value: localPackage.comments == null ? "" : localPackage.comments,
							onChange: handleInputChange,
							disabled: pageMode !== "edit",
							multiline: true,
							rows: 6,
						})}

						<Box m={3} />

						{!localPackage?.id && baselinesFiltered && (
							<>
								<Box width={pageWidth} mt="1rem" mb="0.5rem">
									<Field.Root
										required
										invalid={baselineError !== ""}
										style={{ width: "100%" }}
									>
										{(() => {
											const baselineCollection = createListCollection({
												items: buildSelectCollectionItems({
													items: baselinesFiltered,
													includeEmpty: true,
													placeholder: "Select a baseline...",
												}),
											});

											return (
												<SelectRoot
													name="baseline_id"
													collection={baselineCollection}
													size={sizeToSelectSize(uiSize)}
													width="100%"
													variant="outline"
													invalid={baselineError !== ""}
													disabled={pageMode !== "edit"}
													value={
														localPackage?.baseline_id == null
															? []
															: [String(localPackage.baseline_id)]
													}
													onValueChange={(details) => {
														// Preserve the existing package-edit contract by translating the
														// direct Select value array back to the baseline id shape this
														// screen already stores locally.
														const raw = Array.isArray(details?.value)
															? String(details.value[0] ?? "")
															: "";
														handleChooseBaseline(
															"baseline_id",
															raw === "" ? "" : Number(raw),
														);
													}}
												>
													<SelectLabel>
														Baseline
														<Field.RequiredIndicator />
													</SelectLabel>
													<SelectTrigger
														id="baseline_id"
														aria-invalid={baselineError !== "" ? "true" : undefined}
														aria-describedby="baseline_id-helptext"
													>
														<SelectValueText placeholder="Select a baseline...">
															{renderSelectValueLabel}
														</SelectValueText>
													</SelectTrigger>
													<Portal>
														<SelectContent>
															{baselineCollection.items.map((item) => (
																<SelectItem key={item.value} item={item}>
																	{item.label}
																</SelectItem>
															))}
														</SelectContent>
													</Portal>
												</SelectRoot>
											);
										})()}
										<Field.HelperText id="baseline_id-helptext">
											<span
												style={{
													display: "block",
													minHeight: "1.5em",
													fontSize: "0.75rem",
													color:
														baselineError !== ""
															? "var(--metrea-colors-border-danger, #F04438)"
															: "inherit",
													visibility:
														baselineError !== "" ? "visible" : "hidden",
												}}
											>
												{baselineError !== "" ? baselineError : "\u00A0"}
											</span>
										</Field.HelperText>
									</Field.Root>
								</Box>
							</>
						)}
						{currBaseline?.id && (
							<>
								{renderTextField({
									id: "baseline_name",
									name: "baseline_name",
									label: "Baseline Name",
									value: currBaseline.name,
									disabled: true,
								})}
								<Box m={3} />
								<RecordNav
									callFrom="Auction Package"
									showBack
									{...(pageMode === "edit"
										? {
												...(localPackage.status_id !== 1
													? { showRestore: true }
													: { showArchive: true }),
												showRevert: true,
												showSave: true,
										  }
										: {})}
									onClickBackToList={clickBackToListHandler}
									onClickArchive={clickArchiveHandler}
									onClickRevert={clickRevertHandler}
									onClickSave={clickSaveHandler}
									disabledRevert={!canRevert}
									disabledSave={!canSave}
									hasError={hasValidationError}
								/>

								{localPackage?.aucItemSelected && (
									<PackageItemList
										item={localPackage}
										mode={pageMode}
										onShowAuction={handleSelectPackageItemForAuction}
										onShowItem={handleShowItem}
										onSelectItem={handleSelectItem}
									/>
								)}

								{pageMode === "edit" && (
									<>
										{/* <Box m={3} /> */}
										<RecordNav
											callFrom="Auction Package"
											showBack
											{...(pageMode === "edit"
												? {
														...(localPackage.status_id !== 1
															? { showRestore: true }
															: { showArchive: true }),
														showRevert: true,
														showSave: true,
												  }
												: {})}
											onClickBackToList={clickBackToListHandler}
											onClickArchive={clickArchiveHandler}
											onClickRevert={clickRevertHandler}
											onClickSave={clickSaveHandler}
											disabledRevert={!canRevert}
											disabledSave={!canSave}
											hasError={hasValidationError}
										/>
										{/* <hr /> */}
										{localPackage?.aucBidderSelected && (
											<PackageBidderList
												item={localPackage}
												mode={pageMode}
												onEditItem={handleEditBidder}
												onSelectItem={handleSelectBidder}
											/>
										)}
									</>
								)}
							</>
						)}
					</Stack>
				</>
			)}

			{/* display auction page of the selected packaged item */}
			{selectedPackageItem && (
				<>
					<PackageItemDetails
						aucPkg={localPackage}
						mode={
							localPackage.status_id === 1 &&
							getValuesByPropertyPath(localPackage.auc_package_bidders, "user_id").indexOf(
								Session().currUser.id
							) !== -1
								? pageMode
								: "readOnly"
						}
						onReloadList={handleReloadList}
					/>
				</>
			)}
		</div>
	);
};

export default EditPackage;
